import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface PermissionGuardProps {
  /** Admin section key (matches adminSidebar key) the user must be able to access. */
  permission: string;
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * Route-level guard for admin sections. Superadmins (is_admin) pass through;
 * role-based admins must have the section in their granted permission set.
 * Users with neither are redirected to the admin dashboard.
 */
const PermissionGuard: React.FC<PermissionGuardProps> = ({
  permission,
  children,
  redirectTo = '/admin/dashboard',
}) => {
  const { isAdmin = false, isAdminLoading = false, adminPermissions = [] } = useAuth();

  if (isAdminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (isAdmin || adminPermissions.includes(permission)) {
    return <>{children}</>;
  }

  return <Navigate to={redirectTo} replace />;
};

export default PermissionGuard;
